/**
 * Command Center → Alerts. Prototype: docs/HRMS.jsx line 1943-1968.
 *
 * A popover pinned to the top-right, listing the actionable items the server
 * derived — one row per leave request, review, claim and celebration. The sidebar's
 * count pill and this list read the same response, so they cannot disagree.
 *
 * What this panel owes the reader:
 *
 *  · **Every row goes somewhere.** An alert is a statement that work is waiting, so
 *    each row is a `<Link>`: keyboard-reachable and middle-clickable for free.
 *  · **The destination depends on who is reading.** Alerts are scoped server-side to
 *    what the viewer can act on, so the same `leave-pending` kind means the admin
 *    queue or a manager's Approvals tab. `DESTINATION` below is that mapping and the
 *    only place it lives.
 *  · **The destination is the item, not the list.** A leave row opens that one
 *    request (`?request=<id>`), ready to approve or decline; a review row opens the
 *    schedule filtered to the person it names (`?q=<name>`). What you are looking at
 *    is in the address, so it survives a reload and pastes into Slack.
 *  · **Opening a review alert clears it** for this reader (`dismissOnOpen`). Leave and
 *    claim rows stay until they are decided, at which point the server stops
 *    returning them.
 *  · **It behaves like a dialog.** Escape goes through the shared overlay stack (a
 *    drawer opened on top used to eat the keypress), focus moves into the panel and
 *    returns to the Alerts button on close, and a click outside dismisses it.
 *
 * The API names an icon; this file is the only place that turns that name into a
 * glyph. An unknown name falls back to the bell rather than crashing the panel.
 */
import {
  BellRing,
  CalendarDays,
  ChevronRight,
  ClipboardCheck,
  Gift,
  Receipt,
  UserPlus,
  X,
  type LucideIcon,
} from "lucide-react";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useDismissAlert, type Alert } from "@/hooks/useAlerts";
import { Spinner, useDialog } from "@/components/ui";
import { useMe } from "@/lib/session";
import { LANDING_ROUTE, type Workspace } from "./Sidebar";

const ICONS: Record<string, LucideIcon> = {
  ClipboardCheck,
  CalendarDays,
  Receipt,
  Gift,
  BellRing,
  UserPlus,
};

/**
 * Where each kind of alert sends the reader, per workspace.
 *
 * Keyed on `alert.kind`; the row's `id` is unique per item and carries nothing a
 * route can use. A kind this table does not know about renders as a plain row
 * rather than a link, so the server can add one without a matching client release.
 *
 * A filter appears here only where the destination screen actually reads it.
 * Reviews and Reimbursements read theirs, and both leave queues read `?request=`.
 * A workspace may be absent from a row, which renders it as a plain line.
 */
const DESTINATION: Record<string, Partial<Record<Workspace, string>>> = {
  "review-overdue": { admin: "/admin/reviews?state=Overdue", manager: "/team", employee: "/me/reviews" },
  "review-due-week": {
    // The alert says "this week" and the filter is a fortnight, so this is a superset
    // of what the row counted — but it is the narrowest bucket the screen offers, and
    // the `q=` below pins it to the one person the sentence is about.
    admin: "/admin/reviews?state=Due+soon",
    manager: "/team",
    employee: "/me/reviews",
  },
  // No `employee`: a member decides nothing, so the server never sends a member
  // this kind — the row would be their own request, waiting on somebody else.
  "leave-pending": { admin: "/admin/leave", manager: "/team" },
  "claim-pending": {
    admin: "/admin/reimbursements?status=submitted",
    manager: "/team/reimbursements?status=submitted",
    employee: "/me/reimbursements",
  },
  celebration: LANDING_ROUTE,
  // The reader's own welcome as a new joinee: their first-month checklist.
  welcome: { admin: "/onboarding", manager: "/onboarding", employee: "/onboarding" },
};

/** Screens whose search box the alert's subject should pre-fill. */
const SEARCH_PREFILLING_DESTINATION = "/admin/reviews";

/** Where one alert row points, filter and all. Exported for the test that pins it. */
export function destinationFor(alert: Alert, workspace: Workspace): string | undefined {
  const to = DESTINATION[alert.kind]?.[workspace];
  if (to === undefined) return undefined;

  const [path, search = ""] = to.split("?");
  const params = new URLSearchParams(search);
  if (alert.kind === "leave-pending" && alert.targetId) params.set("request", alert.targetId);
  else if (to.startsWith(SEARCH_PREFILLING_DESTINATION) && alert.subjectName) params.set("q", alert.subjectName);
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

const ROW_STYLE = {
  display: "flex",
  gap: 11,
  padding: "13px 16px",
  alignItems: "center",
  textDecoration: "none",
  color: "inherit",
} as const;

/**
 * The reader-facing name of each kind, for the full Alerts page's type filter and
 * its per-type counts. Order is the order the server lists them in.
 */
export const ALERT_KIND_LABEL: Record<string, string> = {
  "review-overdue": "Overdue reviews",
  "review-due-week": "Reviews due this week",
  "leave-pending": "Leave requests",
  "claim-pending": "Reimbursement claims",
  celebration: "Celebrations",
  welcome: "Welcome",
};

export interface AlertRowProps {
  alert: Alert;
  /** No divider above the first row of a list. */
  first: boolean;
  /** Runs after the row is opened — the panel closes itself here. */
  onOpen?: () => void;
}

/**
 * One alert, as the panel and the full Alerts page both draw it. Opening a review
 * row clears it for this reader (`dismissOnOpen`), wherever it was opened from.
 */
export function AlertRow({ alert, first, onOpen }: AlertRowProps) {
  const me = useMe();
  const dismiss = useDismissAlert();
  const Icon = ICONS[alert.icon] ?? BellRing;
  const to = destinationFor(alert, me.workspace);
  const style = { ...ROW_STYLE, borderTop: first ? "none" : "1px solid var(--line)" };

  const body = (
    <>
      <div
        style={{
          width: 30,
          height: 30,
          borderRadius: 8,
          background: "var(--bg)",
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
        }}
      >
        <Icon size={15} color={alert.colorVar} aria-hidden="true" />
      </div>
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontFamily: "var(--font-body)",
            fontWeight: 600,
            fontSize: 12.5,
            color: "var(--ink)",
            lineHeight: 1.45,
          }}
        >
          {alert.text}
        </div>
        <div
          style={{
            fontFamily: "var(--font-body)",
            fontSize: 10.5,
            color: "var(--muted)",
            marginTop: 3,
          }}
        >
          {alert.when}
        </div>
      </div>
      {to ? <ChevronRight size={15} color="var(--muted)" aria-hidden="true" /> : null}
    </>
  );

  return to ? (
    <Link
      to={to}
      onClick={() => {
        if (alert.dismissOnOpen) dismiss.mutate(alert.id);
        onOpen?.();
      }}
      style={style}
    >
      {body}
    </Link>
  ) : (
    <div style={style}>{body}</div>
  );
}

export interface AlertsPanelProps {
  alerts: readonly Alert[];
  loading?: boolean;
  onClose: () => void;
}

export function AlertsPanel({ alerts, loading = false, onClose }: AlertsPanelProps) {
  // Escape (through the shared stack), the focus trap and focus restoration all come
  // from here; the panel is not a backdrop dialog, so `onOverlayMouseDown` is unused
  // and outside-click is handled below instead.
  const { panelRef } = useDialog<HTMLDivElement>({ onClose });

  // Outside-click dismissal, the way a notification popover is expected to behave.
  //
  // On `click` rather than `mousedown` so the click still reaches what it landed on:
  // dismissing on the press swallowed it, and clicking a name in "Who's out today"
  // with the panel open closed the panel without opening the record — two clicks for
  // one intention.
  //
  // Two things stop the panel from dismissing itself. It listens from the next tick,
  // because the click that opened it is still bubbling towards `document` while this
  // effect runs and would read as a click outside. And it ignores an expanded
  // control, because the Alerts button *toggles*: closing here as well would let the
  // button reopen what it just closed, and the bell would look dead. The control that
  // opened a popover owns closing it.
  useEffect(() => {
    const onDocumentClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target || !panelRef.current) return;
      if (panelRef.current.contains(target)) return;
      if (target.closest('[aria-expanded="true"]')) return;
      onClose();
    };
    const timer = window.setTimeout(
      () => document.addEventListener("click", onDocumentClick),
      0,
    );
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("click", onDocumentClick);
    };
  }, [onClose, panelRef]);

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-label="Alerts"
      tabIndex={-1}
      // Position, width, border and radius live in `styles/shell.css` (`.bp-alerts`),
      // where a media query can turn the popover into a full-screen sheet on a phone.
      className="bp-alerts"
      style={{
        background: "var(--surface)",
        boxShadow: "0 16px 50px rgba(10,10,11,.22)",
        // Under the modal and drawer overlays (60), above the sticky top bar (50).
        // At 80 this popover covered the record drawer it had just opened.
        zIndex: 55,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "13px 16px",
          background: "var(--bg)",
          display: "flex",
          alignItems: "center",
          gap: 9,
        }}
      >
        <BellRing size={15} color="var(--violet)" aria-hidden="true" />
        <div
          style={{
            flex: 1,
            fontFamily: "var(--font-head)",
            fontWeight: 700,
            fontSize: 13.5,
            color: "var(--ink)",
          }}
        >
          Alerts
        </div>
        <button type="button" className="ui-iconbtn" onClick={onClose} aria-label="Close alerts">
          <X size={16} aria-hidden="true" />
        </button>
      </div>

      <div className="bp-alerts__list">
        {loading ? (
          <div style={{ padding: 26, display: "grid", placeItems: "center" }}>
            <Spinner label="Loading alerts" />
          </div>
        ) : null}

        {!loading && alerts.length === 0 ? (
          <div
            style={{
              padding: 26,
              textAlign: "center",
              fontFamily: "var(--font-body)",
              fontSize: 12.5,
              color: "var(--muted)",
            }}
          >
            Nothing new.
          </div>
        ) : null}

        {alerts.map((alert, index) => (
          <AlertRow key={alert.id} alert={alert} first={index === 0} onOpen={onClose} />
        ))}
      </div>

      {/* The panel scrolls through every alert; the page adds filters, search and sort. */}
      {!loading ? (
        <Link to="/alerts" className="bp-alerts__all" onClick={onClose}>
          View all alerts{alerts.length > 0 ? ` (${alerts.length})` : ""}
        </Link>
      ) : null}
    </div>
  );
}

export default AlertsPanel;
